import { type FC, useState } from 'react'
import { Button } from '../../../components/Button/Button'
import { LinkButton } from '../../../components/Button/LinkButton'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { IntegrationServiceId } from '../../../common/integrations'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { useGetUserSchedulingSettingsQuery, useUpdateUserSchedulingSettingsMutation } from '../../../store/scheduling/scheduling'
import { type CalendarProvider, MeetingPlatformProvider } from '../../../models/Scheduling'
import { OnboardingStepHeader } from '../../Growth/Onboarding/OnboardingStepHeader/OnboardingStepHeader'
import { useIntegrations } from '../../Integration/useIntegrations'
import { ConnectedDetail, IntegrationOptionRow, ReconnectDetail } from './IntegrationOptionRow'
import { OnboardingStepContent } from './OnboardingStepFrame'
import {
  CALENDAR_SERVICE_ID_BY_PROVIDER,
  getIntegrationEmail,
  IntegrationConnectionStatus,
  MEETING_PLATFORM_SERVICE_ID,
  type MeetingPlatformOnboardingOption,
  resolveMeetingPlatformOptions,
} from './onboardingIntegrations'

const INTEGRATION_SETTINGS_PATH = '/account/integration-settings/integrations'
const connectPathFor = (serviceId: IntegrationServiceId) => `/account/integration-settings/integrations/${serviceId}/settings?connect`
const RECONNECT_TOOLTIP = "Your meeting platform is connected but Scheduling can't add meetings to it yet. Reconnect your meeting platform to fix this."
const SUBTITLE = 'Zocks adds a join link to every online booking.'

export interface MeetingPlatformStepProps {
  onCompleted?: () => void
}

export const MeetingPlatformStep: FC<MeetingPlatformStepProps> = ({ onCompleted }) => {
  const dispatch = useAppDispatch()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { accountId, id: userId } = authenticatedUser
  const { data: me } = useGetMeQuery({ accountId })
  const { data: userSettings } = useGetUserSchedulingSettingsQuery({ accountId, userId })
  const { isIntegrationAllowed, isLoading: isIntegrationsLoading } = useIntegrations()
  const [updateUserSettings, { isLoading: isSelecting }] = useUpdateUserSchedulingSettingsMutation()
  const [selectedProvider, setSelectedProvider] = useState<MeetingPlatformProvider>()

  if (!userSettings || isIntegrationsLoading) {
    return null
  }

  const options = resolveMeetingPlatformOptions(userSettings.integrations, isIntegrationAllowed)
  const nativeOption = options.find((option) => option.isNative)
  const zoomOption = options.find((option) => option.provider === MeetingPlatformProvider.ZOOM)

  const selectPlatform = async (provider: MeetingPlatformProvider) => {
    const result = await updateUserSettings({
      accountId,
      userId,
      update: { integrations: { ...userSettings.integrations, selectedMeetingPlatform: provider } },
    })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not select the meeting platform', messages: ['Please try again.'] }))
    } else {
      onCompleted?.()
    }
  }

  const reconnectPathFor = (option: MeetingPlatformOnboardingOption): string =>
    option.isNative
      ? connectPathFor(CALENDAR_SERVICE_ID_BY_PROVIDER[userSettings.integrations.selectedCalendar as CalendarProvider])
      : connectPathFor(IntegrationServiceId.ZOOM)

  const manageLink = (
    <LinkButton variant='primaryLinkButton' to={INTEGRATION_SETTINGS_PATH} className='text-decoration-none'>
      Manage your meeting platform integrations
    </LinkButton>
  )

  if (!nativeOption) {
    return (
      <>
        <OnboardingStepHeader icon={<i className='bi bi-camera-video' />} title='Confirm your meeting platform' subtitle={SUBTITLE} />
        {manageLink}
      </>
    )
  }

  const hasSelectableZoomOption = zoomOption != null && zoomOption.status !== IntegrationConnectionStatus.NOT_CONNECTED

  if (!hasSelectableZoomOption) {
    const isReconnect = nativeOption.status === IntegrationConnectionStatus.NEEDS_RECONNECT
    const nativeIconServiceId = MEETING_PLATFORM_SERVICE_ID[nativeOption.provider]

    return (
      <>
        <OnboardingStepHeader icon={<i className='bi bi-camera-video' />} title='Confirm your meeting platform' subtitle={SUBTITLE} />
        <OnboardingStepContent>
          <div className='d-flex flex-column gap-3 p-3 border rounded-3 zocks-border-color-gray-300 w-100'>
            <div className='d-flex align-items-center gap-2'>
              {nativeIconServiceId ? <IntegrationIcon serviceId={nativeIconServiceId} width='1.5rem' height='1.5rem' /> : null}
              <span className='d-flex flex-column overflow-hidden'>
                <span className='paragraph-small fw-bold zocks-text-gray-900'>{nativeOption.label}</span>
                <span className='paragraph-small zocks-text-gray-600'>{nativeOption.viaLabel}</span>
              </span>
            </div>
            {isReconnect ? (
              <LinkButton to={reconnectPathFor(nativeOption)} variant='primary' className='align-self-stretch'>
                Reconnect
              </LinkButton>
            ) : (
              <Button variant='primary' className='align-self-stretch' loading={isSelecting} onClick={() => void selectPlatform(nativeOption.provider)}>
                Select platform
              </Button>
            )}
          </div>
          {zoomOption ? manageLink : null}
        </OnboardingStepContent>
      </>
    )
  }

  const selectedOption =
    options.find((option) => option.provider === selectedProvider) ??
    options.find((option) => option.provider === userSettings.integrations.selectedMeetingPlatform)
  const isSelectedReconnect = selectedOption?.status === IntegrationConnectionStatus.NEEDS_RECONNECT

  return (
    <>
      <OnboardingStepHeader icon={<i className='bi bi-camera-video' />} title='Choose your meeting platform' subtitle={SUBTITLE} />
      <OnboardingStepContent>
        <div role='radiogroup' aria-label='Meeting platform' className='d-flex flex-column gap-2 w-100'>
          {options.map((option) => (
            <IntegrationOptionRow
              key={option.provider}
              serviceId={option.serviceId ?? MEETING_PLATFORM_SERVICE_ID[option.provider]}
              label={option.label}
              detail={
                option.isNative ? (
                  <span className='zocks-text-gray-600'>{option.viaLabel}</span>
                ) : option.status === IntegrationConnectionStatus.READY ? (
                  <ConnectedDetail email={getIntegrationEmail(me?.integrations, option.serviceId as IntegrationServiceId)} />
                ) : (
                  <ReconnectDetail email={getIntegrationEmail(me?.integrations, option.serviceId as IntegrationServiceId)} tooltip={RECONNECT_TOOLTIP} />
                )
              }
              selected={option.provider === selectedOption?.provider}
              onSelect={() => setSelectedProvider(option.provider)}
            />
          ))}
        </div>
        {selectedOption && isSelectedReconnect ? (
          <LinkButton to={reconnectPathFor(selectedOption)} variant='primary' className='align-self-stretch'>
            Reconnect
          </LinkButton>
        ) : (
          <Button
            variant='primary'
            className='align-self-stretch'
            disabled={!selectedOption}
            loading={isSelecting}
            onClick={() => (selectedOption ? void selectPlatform(selectedOption.provider) : undefined)}
          >
            Select platform
          </Button>
        )}
      </OnboardingStepContent>
    </>
  )
}
